import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../services/api";

/**
 * Interface que define a estrutura das configurações de branding
 */
export interface IBranding {
  platformName: string;
  logoUrl: string;
  iconUrl: string;
  faviconUrl: string;
  colors: {
    primary: string;
    primaryFocus: string;
    secondary: string;
    background: string;
    sidebarStart: string;
    sidebarEnd: string;
    textDark: string;
    headerBackground: string;
    headerAction: string;
  };
}

interface BrandingContextType {
  branding: IBranding;
  isLoading: boolean;
}

/**
 * Valores padrão carregados das variáveis de ambiente
 * Usados como fallback caso não consiga carregar do backend
 */
const getDefaultBranding = (): IBranding => ({
  platformName: process.env.REACT_APP_PLATFORM_NAME || "ReferralHub",
  logoUrl: process.env.REACT_APP_LOGO_URL || "",
  iconUrl: process.env.REACT_APP_ICON_URL || "",
  faviconUrl: process.env.REACT_APP_FAVICON_URL || "",
  colors: {
    primary: process.env.REACT_APP_PRIMARY_COLOR || "#DDCA92",
    primaryFocus: process.env.REACT_APP_PRIMARY_FOCUS || "#A5945F",
    secondary: process.env.REACT_APP_SECONDARY_COLOR || "#7b809a",
    background: process.env.REACT_APP_BACKGROUND_COLOR || "#EEF1F6",
    sidebarStart: process.env.REACT_APP_SIDEBAR_START || "#0B2F36",
    sidebarEnd: process.env.REACT_APP_SIDEBAR_END || "#000000",
    textDark: process.env.REACT_APP_TEXT_DARK || "#344767",
    headerBackground: process.env.REACT_APP_HEADER_BACKGROUND_COLOR || "",
    headerAction: process.env.REACT_APP_HEADER_ACTION_COLOR || "#ffffff",
  },
});

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined,
);

/**
 * Mescla cores da API sem apagar valores do frontend/.env quando o backend devolve vazio.
 * O backend usa '' em headerBackground para significar "usar gradiente", o que sobrescrevia
 * REACT_APP_HEADER_BACKGROUND_COLOR após o fetch.
 */
function mergeBrandingColors(
  prev: IBranding["colors"],
  incoming: Partial<IBranding["colors"]> | undefined,
): IBranding["colors"] {
  if (!incoming) {
    return prev;
  }
  const merged: IBranding["colors"] = { ...prev, ...incoming };
  if (!(incoming.headerBackground ?? "").trim()) {
    merged.headerBackground = prev.headerBackground;
  }
  return merged;
}

interface BrandingProviderProps {
  children: ReactNode;
}

/**
 * Provider que gerencia as configurações de branding da plataforma
 * Tenta carregar do backend, se falhar usa valores do .env
 */
export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
}) => {
  const [branding, setBranding] = useState<IBranding>(getDefaultBranding());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // Tenta carregar configurações do backend
        const response = await api.get("/config/branding");
        if (response.data.success && response.data.data) {
          const d = response.data.data as IBranding;
          setBranding((prev) => ({
            ...prev,
            ...d,
            colors: mergeBrandingColors(prev.colors, d.colors),
          }));
        }
      } catch {
        // Em caso de erro, mantém os valores do .env (já carregados)
        console.log("Usando configurações de branding do .env");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBranding();
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Hook para acessar as configurações de branding em qualquer componente
 */
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
};
